import { useEffect, useMemo, useState } from "react";import {
  LayoutDashboard, Users, House, CalendarDays, ClipboardList, Search, Plus,
  Siren, Bell, ChevronRight, MapPin, Menu, X, ArrowUpRight, Building2, Radio,
  ScrollText, HandCoins, Compass, LogOut, AlertTriangle, Megaphone, Map, Wallet, FileScan, Vote, Settings2,
  MessageSquare, PieChart, Newspaper, ShieldCheck, FileText, Landmark,
  Send, Trash2, Trophy,
} from "lucide-react";
import { toast } from "sonner";
import { http, nav } from "./api";
import { UndoToast } from "./shared";
import Dashboard from "./pages/Dashboard";
import VoterSearchPalette from "./pages/VoterSearchPalette";
import People from "./pages/People";
import AdminMembers from "./pages/AdminMembers";
import Forms from "./pages/Forms";
import Schemes from "./pages/Schemes";
import Households from "./pages/Households";
import Organizations from "./pages/Organizations";
import Complaints from "./pages/Complaints";
import Promises from "./pages/Promises";
import Tasks from "./pages/Tasks";
import FieldOps from "./pages/FieldOps";
import Volunteers from "./pages/Volunteers";
import Campaigns from "./pages/Campaigns";
import Events from "./pages/Events";
import Broadcasts from "./pages/Broadcasts";
import Communicate from "./pages/Communicate";
import Audit from "./pages/Audit";
import LiveMap from "./pages/LiveMap";
import Finance from "./pages/Finance";
import VoterRoll from "./pages/VoterRoll";
import ElectionDay from "./pages/ElectionDay";
import TenantSettings from "./pages/TenantSettings";
import BoothChat from "./pages/BoothChat";
import SentimentMap from "./pages/SentimentMap";
import WeeklyDigest from "./pages/WeeklyDigest";
import RecycleBin from "./pages/RecycleBin";
import Leaderboard from "./pages/Leaderboard";
import BoothOwners from "./pages/BoothOwners";
import Duplicates from "./pages/Duplicates";
import AdviceCMS from "./pages/AdviceCMS";
import SystemSettings from "./pages/SystemSettings";
import Profile from "./pages/Profile";

const iconMap = {
  dashboard: LayoutDashboard, livemap: Map, sentiment: PieChart, digest: Newspaper,
  people: Users, adminmembers: ShieldCheck, households: House, organizations: Building2, voterroll: FileScan, electionday: Vote,
  complaints: AlertTriangle, promises: HandCoins, tasks: ClipboardList, field: Compass,
  volunteers: Users, chat: MessageSquare, campaigns: Megaphone, events: CalendarDays, broadcasts: Radio,
  communicate: Send, forms: FileText, schemes: Landmark, advice: FileText,
  finance: Wallet, audit: ScrollText, tenant: Settings2, recyclebin: Trash2, leaderboard: Trophy, boothowners: ShieldCheck, duplicates: Users,
  system: Settings2, person: Users,
};

const pageMap = {
  dashboard: Dashboard, livemap: LiveMap, sentiment: SentimentMap, digest: WeeklyDigest,
  people: People, adminmembers: AdminMembers, households: Households, organizations: Organizations,
  voterroll: VoterRoll, electionday: ElectionDay,
  complaints: Complaints, promises: Promises, tasks: Tasks, field: FieldOps,
  volunteers: Volunteers, chat: BoothChat, campaigns: Campaigns, events: Events,
  broadcasts: Broadcasts, communicate: Communicate, forms: Forms, schemes: Schemes, advice: AdviceCMS,
  finance: Finance, audit: Audit, tenant: TenantSettings, recyclebin: RecycleBin, leaderboard: Leaderboard, boothowners: BoothOwners, duplicates: Duplicates,
  system: SystemSettings, person: Profile,
};

export default function Shell({ user, setUser }) {
  const [page, setPage] = useState("dashboard");
  const [pageContext, setPageContext] = useState(null);
  const [open, setOpen] = useState(false);
  const [sos, setSos] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [tenant, setTenant] = useState(null);
  const [globalBrand, setGlobalBrand] = useState(null);
  const [chatUnread, setChatUnread] = useState(0);
  const [alerts, setAlerts] = useState([]);
  const [showAlerts, setShowAlerts] = useState(false);

  useEffect(() => {
    http.get("/tenants/current").then((r) => {
      const t = r.data.tenant;
      setTenant(t);
    }).catch(() => {});
    // Global branding overrides tenant colors app-wide
    http.get("/branding/effective").then((r) => {
      const b = r.data || {};
      setGlobalBrand(b);
      const root = document.documentElement;
      if (b.primary_color) root.style.setProperty("--teal", b.primary_color);
      if (b.accent_color) root.style.setProperty("--amber", b.accent_color);
    }).catch(() => {});
  }, []);

  // Global "open person profile" listener — any PersonLink in the app can fire
  // `wardcrm:open-person` and Shell will switch to the Profile page for it.
  useEffect(() => {
    const handler = (e) => {
      const cid = e.detail?.contact_id;
      if (!cid) return;
      setPageContext({ contact_id: cid, from: page });
      setPage("person");
      setShowResults(false);
      setOpen(false);
    };
    window.addEventListener("wardcrm:open-person", handler);
    return () => window.removeEventListener("wardcrm:open-person", handler);
  }, [page]);

  useEffect(() => {
    // Global 15-second poll for chat unread + alerts
    const seenBcastAlerts = new Set();
    const seenRollAlerts = new Set();
    const tick = async () => {
      try {
        const [u, a] = await Promise.all([http.get("/chat/unread"), http.get("/alerts")]);
        setChatUnread(u.data.total || 0);
        const rows = a.data || [];
        setAlerts(rows);
        // Phase 30.3 — pop a delivery-digest toast for new broadcast_finished alerts
        rows
          .filter((x) => x.type === "broadcast_finished" && !x.acknowledged && !seenBcastAlerts.has(x.alert_id))
          .forEach((x) => {
            seenBcastAlerts.add(x.alert_id);
            const failed = x.totals?.failed || 0;
            const fn = failed ? toast.warning : toast.success;
            fn(x.message, {
              description: failed
                ? "Open Broadcasts → Recipients to inspect failed rows."
                : "All recipients cleared. Nice work!",
              action: {
                label: "Open Broadcasts",
                onClick: () => setPage("broadcasts"),
              },
            });
          });
        // Phase 30.9 — pop an import-success toast for new voter_roll_ready alerts
        rows
          .filter((x) => x.type === "voter_roll_ready" && !x.acknowledged && !seenRollAlerts.has(x.alert_id))
          .forEach((x) => {
            seenRollAlerts.add(x.alert_id);
            const created = x.profiles_created || 0;
            if (created > 0) {
              toast.success(`Just added ${created} voter${created === 1 ? "" : "s"} from ${x.polling_station_name || "voter roll"}`, {
                description: `${x.profiles_matched || 0} matched to existing contacts. Review and bulk-edit in one shot.`,
                action: {
                  label: "Review imports",
                  onClick: () => {
                    setPageContext({ recent_import_hours: 1, from: "voter_roll_alert", scan_id: x.scan_id });
                    setPage("people");
                  },
                },
              });
            }
          });
      } catch { /* ignore */ }
    };
    tick();
    const t = setInterval(tick, 15000);
    return () => clearInterval(t);
  }, [page]);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(() => {
      http.get(`/search?q=${encodeURIComponent(q)}`).then((r) => { setResults(r.data.results || []); setShowResults(true); }).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const grouped = useMemo(() => {
    const gs = {};
    const isSuper = user?.role === "Super Admin";
    nav.forEach((n) => {
      if (n.superAdminOnly && !isSuper) return;
      gs[n.group] = gs[n.group] || []; gs[n.group].push(n);
    });
    return gs;
  }, [user]);

  const logout = async () => {
    await http.post("/auth/logout").catch(() => {});
    setUser(false);
  };

  const CurrentPage = pageMap[page] || Dashboard;
  const currentLabel = nav.find((n) => n.id === page)?.label || "Workspace";

  return (
    <div className="app-shell">
      <VoterSearchPalette />
      <UndoToast />
      <aside className={`sidebar ${open ? "is-open" : ""}`}>
        <div className="brand">
          <div className="mark">{(globalBrand?.logo_url || tenant?.branding?.logo_url) ? <img src={globalBrand?.logo_url || tenant?.branding?.logo_url} alt="" style={{ width: 24, height: 24, objectFit: "contain" }} /> : "84"}</div>
          <div><b>{globalBrand?.brand_name || tenant?.branding?.display_name || tenant?.org_name || "Ward 7 CRM"}</b><span>{globalBrand?.brand_tagline_en || (tenant?.plan ? `Jonogoner Swarthe Ward 7 · ${tenant.plan}` : "Jonogoner Swarthe Ward 7")}</span></div>
          <button className="mobile-close" data-testid="sidebar-close-button" onClick={() => setOpen(false)}><X size={18} /></button>
        </div>
        <div className="territory"><MapPin size={15} /><span>Ward 7 · 24 booths</span><ChevronRight size={14} /></div>
        <nav>
          {Object.entries(grouped).map(([group, items]) => (
            <div key={group} className="nav-group">
              <div className="nav-group-title">{group}</div>
              {items.map((n) => {
                const Icon = iconMap[n.id] || LayoutDashboard;
                return (
                  <button key={n.id} className={page === n.id ? "active" : ""}
                    data-testid={`nav-${n.id}-button`}
                    onClick={() => { setPage(n.id); setOpen(false); }}>
                    <Icon size={17} /><span>{n.label}</span>
                    {n.id === "chat" && chatUnread > 0 && <span className="nav-badge" data-testid="chat-unread-badge">{chatUnread}</span>}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>
        <div className="sidebar-bottom">
          <button data-testid="logout-button" onClick={logout}><LogOut size={16} />Sign out</button>
          <button data-testid="settings-button"><span className="avatar small">{user.name?.split(" ").map((x) => x[0]).join("").slice(0,2)}</span>{user.name}</button>
        </div>
      </aside>

      <main>
        <header className="topbar">
          <button className="mobile-menu" data-testid="sidebar-open-button" onClick={() => setOpen(true)}><Menu size={21} /></button>
          <div className="search" style={{ position: "relative" }}>
            <Search size={18} />
            <input data-testid="global-search-input" placeholder="Search people, complaints, orgs…"
              value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => setShowResults(true)}
              onBlur={() => setTimeout(() => setShowResults(false), 200)} />
            <kbd data-testid="open-voter-palette" title="Voter journey search"
              onMouseDown={(e) => { e.preventDefault(); window.dispatchEvent(new KeyboardEvent("keydown", { key: "k", ctrlKey: true })); }}
              style={{ cursor: "pointer" }}>⌘ K</kbd>
            {showResults && results.length > 0 && (
              <div className="search-results" data-testid="global-search-results">
                {results.map((r, i) => (
                  <div
                    key={i}
                    className={`search-result ${r.type === "contact" ? "clickable" : ""}`}
                    data-testid={`search-result-${i}`}
                    onMouseDown={(e) => {
                      if (r.type !== "contact") return;
                      e.preventDefault();
                      const cid = r.id || r.entity_id || r.contact_id;
                      if (cid) {
                        setPageContext({ contact_id: cid, from: page });
                        setPage("person");
                        setShowResults(false);
                        setQ("");
                      }
                    }}
                  >
                    <span className={`chip ${r.type}`}>{r.type}</span>
                    <div><b>{r.title}</b><small>{r.meta}</small></div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="top-actions">
            <button className="lang" data-testid="language-toggle-button">বাংলা <span>EN</span></button>
            <div style={{ position: "relative" }}>
              <button className="icon-button" data-testid="notifications-button" onClick={() => setShowAlerts((s) => !s)}>
                <Bell size={19} />
                {alerts.length > 0 && <i className="alert-dot" />}
              </button>
              {showAlerts && (
                <div className="alerts-dropdown" data-testid="alerts-dropdown">
                  <div className="alerts-head"><b>Alerts ({alerts.length})</b>
                    <button className="link" data-testid="alerts-check-button" onClick={async () => {
                      await http.post("/alerts/check");
                      const r = await http.get("/alerts"); setAlerts(r.data);
                    }}>Check now</button>
                  </div>
                  {alerts.length === 0 ? (
                    <p className="hint" style={{ padding: 20, textAlign: "center" }}>All clear — no active alerts.</p>
                  ) : alerts.map((a, i) => {
                    const isVoterRoll = a.type === "voter_roll_ready" || a.type === "voter_roll_error";
                    const isBcast = a.type === "broadcast_finished";
                    const openScan = () => {
                      if (isVoterRoll && a.scan_id) {
                        setPageContext({ scan_id: a.scan_id });
                        setPage("voterroll");
                        setShowAlerts(false);
                      } else if (isBcast) {
                        setPage("broadcasts");
                        setShowAlerts(false);
                      }
                    };
                    return (
                      <div key={a.alert_id} className={`alert-row ${a.severity}`} data-testid={`alert-row-${i}`}
                        onClick={openScan}
                        style={{ cursor: (isVoterRoll && a.scan_id) || isBcast ? "pointer" : "default" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                          {isVoterRoll && <span className="chip blue" data-testid={`alert-chip-${i}`} style={{ fontSize: 10 }}><FileScan size={10} /> Voter Roll</span>}
                          {a.type === "org_tag" && <span className="chip green" style={{ fontSize: 10 }}>Org tag</span>}
                          {isBcast && <span className="chip amber" data-testid={`alert-chip-${i}`} style={{ fontSize: 10 }}><Send size={10} /> Broadcast</span>}
                          <b>{a.message}</b>
                        </div>
                        <small>{new Date(a.created_at).toLocaleString()}</small>
                        <div style={{ display: "flex", gap: 8, marginTop: 4 }} onClick={(e) => e.stopPropagation()}>
                          <button className="link" data-testid={`alert-ack-${i}`} onClick={async () => {
                            await http.patch(`/alerts/${a.alert_id}/ack`);
                            setAlerts((cur) => cur.filter((x) => x.alert_id !== a.alert_id));
                          }}>Acknowledge</button>
                          {user.role === "Super Admin" && (
                            <button className="link danger" data-testid={`alert-delete-${i}`} onClick={async () => {
                              if (!window.confirm("Delete this alert permanently?")) return;
                              await http.delete(`/alerts/${a.alert_id}`);
                              setAlerts((cur) => cur.filter((x) => x.alert_id !== a.alert_id));
                            }}>Delete</button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <button className="profile-button" data-testid="user-profile-button">
              {user.picture ? <img src={user.picture} alt="" className="avatar" style={{ objectFit: "cover" }} /> :
                <span className="avatar">{user.name?.split(" ").map((x) => x[0]).join("").slice(0,2)}</span>}
              <span><b>{user.name}</b><small>{user.role}</small></span>
              <ChevronRight size={15} />
            </button>
          </div>
        </header>

        <div className="content">
          <CurrentPage
            user={user}
            setPage={setPage}
            setPageContext={setPageContext}
            pageContext={pageContext}
            clearPageContext={() => setPageContext(null)}
            contactId={pageContext?.contact_id}
            onBack={() => { const from = pageContext?.from || "people"; setPageContext(null); setPage(from); }}
          />
        </div>
      </main>

      <button className="sos" data-testid="sos-emergency-button" onClick={() => setSos(true)}>
        <Siren size={18} /><span>Emergency SOS</span>
      </button>
      <button className="quick-add" data-testid="global-quick-action-button" onClick={() => setPage("people")}>
        <Plus size={22} />
      </button>

      {sos && (
        <div className="modal-backdrop">
          <div className="sos-modal">
            <Siren size={28} />
            <h2>Emergency response</h2>
            <p>Share your live location with all Admin and Member users?</p>
            <button data-testid="sos-confirm-button" onClick={() => setSos(false)}>Hold to confirm · 3 sec</button>
            <button className="cancel" data-testid="sos-cancel-button" onClick={() => setSos(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

